import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { saveOfflineCredentials, authenticateOffline, isOnline, hasOfflineCredentials, getOfflineCredentialsInfo } from '../utils/offlineAuth';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Timeout de inactividad: 30 minutos en milisegundos
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(0);

  // Interceptor global: si el backend responde 401 por SESIÓN REEMPLAZADA (se inició sesión
  // en otro dispositivo/lugar con sesión única), cerrar sesión limpiamente y avisar. Sólo
  // reacciona a ese caso puntual (no a otros 401), y re-lanza el error para el llamador.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (resp) => resp,
      (error) => {
        const detail = error?.response?.data?.detail;
        // SESION_CERRADA: la cuenta fue eliminada o su rol venció (2026-11-04).
        const reemplazada = typeof detail === 'string' && detail.includes('SESION_REEMPLAZADA');
        const cerrada = typeof detail === 'string' && detail.startsWith('SESION_CERRADA');
        if (error?.response?.status === 401 && (reemplazada || cerrada)) {
          localStorage.removeItem('token');
          localStorage.setItem('session_expired', cerrada ? 'cerrada' : 'replaced');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Función para cerrar sesión por inactividad
  const logoutByInactivity = useCallback(() => {
    console.log('Sesión cerrada por inactividad');
    setShowTimeoutWarning(false);
    localStorage.removeItem('token');
    localStorage.setItem('session_expired', 'inactivity');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Función para reiniciar el timer de inactividad
  const resetInactivityTimer = useCallback(() => {
    // Limpiar timers existentes
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    setShowTimeoutWarning(false);

    if (token && user) {
      // Mostrar advertencia 2 minutos antes del cierre
      warningTimeoutRef.current = setTimeout(() => {
        setShowTimeoutWarning(true);
      }, INACTIVITY_TIMEOUT - 2 * 60 * 1000);

      // Cerrar sesión después del timeout completo
      timeoutRef.current = setTimeout(() => {
        logoutByInactivity();
      }, INACTIVITY_TIMEOUT);
    }
  }, [token, user, logoutByInactivity]);

  // Registrar eventos de actividad del usuario
  useEffect(() => {
    if (!token || !user) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Throttle: 'scroll'/'mousedown'/'click' pueden disparar decenas de eventos por
    // segundo. Reprogramar los timers en cada uno satura el hilo principal al hacer
    // scroll en listas. Basta reiniciar el timer de inactividad como mucho 1 vez cada
    // 5s (el timeout es de 30 min, la precisión de 5s es irrelevante).
    const handleActivity = () => {
      const ahora = Date.now();
      if (ahora - lastActivityRef.current < 5000) return;
      lastActivityRef.current = ahora;
      resetInactivityTimer();
    };

    // Agregar listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Iniciar timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    };
  }, [token, user, resetInactivityTimer]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      // Si es una sesión offline, usar datos guardados
      const isOfflineSession = localStorage.getItem('isOfflineSession') === 'true';
      if (isOfflineSession || !isOnline()) {
        const offlineUserData = localStorage.getItem('asm_offline_user_data');
        if (offlineUserData) {
          const userData = JSON.parse(offlineUserData);
          setUser(userData);
          console.log('👤 Usuario cargado desde datos offline');
          return;
        }
      }
      
      // Intentar obtener usuario del servidor
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Actualizar datos offline con la información más reciente
      localStorage.setItem('asm_offline_user_data', JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching user:', error);
      
      // Si hay error de red pero tenemos datos offline, usarlos
      if (!isOnline() || error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        const offlineUserData = localStorage.getItem('asm_offline_user_data');
        if (offlineUserData) {
          const userData = JSON.parse(offlineUserData);
          setUser(userData);
          localStorage.setItem('isOfflineSession', 'true');
          console.log('👤 Usuario cargado desde datos offline (fallback)');
          return;
        }
      }
      
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    // Verificar si estamos online
    if (!isOnline()) {
      // Intentar autenticación offline
      console.log('🔌 Sin conexión - Intentando login offline...');
      const offlineResult = await authenticateOffline(email, password);
      
      if (offlineResult.success) {
        console.log('✅ Login offline exitoso');
        localStorage.setItem('token', offlineResult.token);
        localStorage.setItem('isOfflineSession', 'true');
        localStorage.removeItem('session_expired');
        setToken(offlineResult.token);
        setUser(offlineResult.user);
        // No configurar axios header porque no hay servidor
        return offlineResult.user;
      } else {
        throw new Error(offlineResult.error);
      }
    }
    
    // Login online normal
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token, user } = response.data;
      
      // Guardar token y usuario
      localStorage.setItem('token', token);
      localStorage.removeItem('session_expired');
      localStorage.removeItem('isOfflineSession');
      setToken(token);
      setUser(user);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Guardar credenciales para uso offline
      await saveOfflineCredentials(email, password, user);
      
      return user;
    } catch (error) {
      // Si falla por problemas de red, intentar offline
      if (!navigator.onLine || error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        console.log('🔌 Error de red - Intentando login offline...');
        const offlineResult = await authenticateOffline(email, password);
        
        if (offlineResult.success) {
          console.log('✅ Login offline exitoso (fallback)');
          localStorage.setItem('token', offlineResult.token);
          localStorage.setItem('isOfflineSession', 'true');
          localStorage.removeItem('session_expired');
          setToken(offlineResult.token);
          setUser(offlineResult.user);
          return offlineResult.user;
        }
      }
      throw error;
    }
  };

  const register = async (email, password, full_name) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name
    });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    setToken(token);
    setUser(user);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return user;
  };

  const logout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    setShowTimeoutWarning(false);
    localStorage.removeItem('token');
    localStorage.removeItem('isOfflineSession');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  // Función para extender la sesión manualmente
  const extendSession = () => {
    resetInactivityTimer();
  };

  // Estado de conexión
  const [isOfflineMode, setIsOfflineMode] = useState(!navigator.onLine);
  
  // Escuchar cambios en la conexión
  useEffect(() => {
    const handleOnline = () => {
      setIsOfflineMode(false);
      console.log('🌐 Conexión restaurada');
      // Si tenemos token offline, intentar renovar sesión online
      const isOfflineSession = localStorage.getItem('isOfflineSession') === 'true';
      if (isOfflineSession && token) {
        // Podríamos intentar reautenticar, pero por ahora solo notificamos
        console.log('💡 Sesión offline activa - Sincronización disponible');
      }
    };
    
    const handleOffline = () => {
      setIsOfflineMode(true);
      console.log('📴 Sin conexión - Modo offline activado');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [token]);

  // Información de credenciales offline disponibles
  const offlineCredentialsInfo = getOfflineCredentialsInfo();

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      token,
      showTimeoutWarning,
      extendSession,
      isOfflineMode,
      hasOfflineCredentials: hasOfflineCredentials(),
      offlineCredentialsInfo
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
